import { supabase } from "../supabaseClient";

export const COMPARE_STATS = [
  { key: "ppg", label: "Points", unit: "PPG", higherIsBetter: true },
  { key: "rpg", label: "Rebounds", unit: "RPG", higherIsBetter: true },
  { key: "apg", label: "Assists", unit: "APG", higherIsBetter: true },
  {
    key: "tpmpg",
    label: "3-Pointers Made",
    unit: "3PM/G",
    higherIsBetter: true,
  },
  {
    key: "twpmpg",
    label: "2-Pointers Made",
    unit: "2PM/G",
    higherIsBetter: true,
  },
  {
    key: "ftmpg",
    label: "Free Throws Made",
    unit: "FTM/G",
    higherIsBetter: true,
  },
  { key: "pfpg", label: "Personal Fouls", unit: "PF/G", higherIsBetter: false },
  {
    key: "gp",
    label: "Games Played",
    unit: "GP",
    higherIsBetter: true,
    isCount: true,
  },
];

export function hexLuminance(hex) {
  const c = (hex || "374151").replace("#", "");
  const r = parseInt(c.slice(0, 2), 16) / 255;
  const g = parseInt(c.slice(2, 4), 16) / 255;
  const b = parseInt(c.slice(4, 6), 16) / 255;
  const lin = (v) =>
    v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

export function aggregateStats(rows, gameType) {
  const map = {};
  for (const row of rows) {
    if (
      !row.game ||
      row.game.game_type !== gameType ||
      row.game.status !== "finished"
    )
      continue;
    if (!row.player || !row.player.team_id) continue;
    const isDNP =
      row.points === 0 &&
      row.rebounds === 0 &&
      row.assists === 0 &&
      row.three_pm === 0 &&
      row.two_pm === 0 &&
      row.ft_made === 0 &&
      (row.personal_fouls || 0) === 0 &&
      (row.technical_fouls || 0) === 0;
    if (isDNP) continue;
    const pid = row.player_id;
    if (!map[pid]) {
      const t = row.player.team || {};
      map[pid] = {
        id: pid,
        name: row.player.name,
        jersey: row.player.jersey_number,
        team: t.name || null,
        team_id: row.player.team_id,
        team_primary_color: t.primary_color || null,
        team_secondary_color: t.secondary_color || null,
        team_gradient: t.gradient || null,
        _games: new Set(),
        pts: 0,
        reb: 0,
        ast: 0,
        pf: 0,
        t3: 0,
        t2: 0,
        ft: 0,
      };
    }
    const p = map[pid];
    p._games.add(row.game.id);
    p.pts += row.points;
    p.reb += row.rebounds;
    p.ast += row.assists;
    p.pf += row.personal_fouls;
    p.t3 += row.three_pm;
    p.t2 += row.two_pm;
    p.ft += row.ft_made;
  }
  return Object.values(map).map((p) => {
    const gp = p._games.size;
    return {
      ...p,
      gp,
      ppg: p.pts / gp,
      rpg: p.reb / gp,
      apg: p.ast / gp,
      pfpg: p.pf / gp,
      tpmpg: p.t3 / gp,
      twpmpg: p.t2 / gp,
      ftmpg: p.ft / gp,
    };
  });
}

export async function fetchSeasonStats(seasonId) {
  const PAGE = 1000;
  let data = [],
    from = 0;
  while (true) {
    const { data: chunk, error } = await supabase
      .from("game_stats")
      .select(
        `player_id, points, rebounds, assists, personal_fouls, technical_fouls, three_pm, two_pm, ft_made,
        game:games!game_stats_game_id_fkey(id, game_type, season_id, status),
        player:players!game_stats_player_id_fkey(id, name, jersey_number, team_id,
          team:teams!players_team_id_fkey(id, name, primary_color, secondary_color, gradient))`,
      )
      .eq("game.season_id", seasonId)
      .range(from, from + PAGE - 1);
    if (error) break;
    data = data.concat(chunk || []);
    if (!chunk || chunk.length < PAGE) break;
    from += PAGE;
    if (from > 100000) break;
  }
  return data;
}

export async function fetchPlayerProfile(playerId) {
  const { data: pr } = await supabase
    .from("players")
    .select("id, user_id")
    .eq("id", playerId)
    .maybeSingle();
  if (!pr || !pr.user_id) return null;
  const { data: prof } = await supabase
    .from("profiles")
    .select("avatar_url, avatar_crop")
    .eq("id", pr.user_id)
    .maybeSingle();
  return prof || null;
}
